import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useServices } from "../../context/ServiceProvider";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Typography,
  Tag,
  Button,
  List,
  Spin,
  Empty,
  Alert,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useRecents } from "../../hooks/useRecents";
import CreateProjectObjectiveModal from "./CreateProjectObjectiveModal";
import CloneProjectObjectiveModal from "./CloneProjectObjectiveModal";
import { resetBreadcrumbs } from "../../store/slices/navigationSlice";
import { getObjective } from "../../services/salesforceProjectApi";

export default function ProjectObjectiveList() {
  const { projectObjective } = useServices();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const dispatch = useDispatch();

  // Dropdown search state
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = React.useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectObjective.fetchAllProjectObjectives();
      setRecords(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [projectObjective]);

  // Dropdown search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const term = (query || "").trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await projectObjective.searchProjectObjectives(term);
        let hits = Array.isArray(data) ? data : [];
        if (
          (!hits || hits.length === 0) &&
          /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(term)
        ) {
          // Fallback to direct ID fetch
          const direct = await getObjective(term.trim());
          const pick = Array.isArray(direct)
            ? direct[0]
            : direct?.data && Array.isArray(direct.data)
              ? direct.data[0]
              : direct?.data?.Id
                ? direct.data
                : direct?.Id
                  ? direct
                  : null;
          hits = pick ? [pick] : [];
        }
        setSearchResults(hits);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, projectObjective]);

  useEffect(() => {
    dispatch(resetBreadcrumbs());
  }, [dispatch]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const columns = useMemo(
    () => [
      {
        title: "Status",
        dataIndex: "Status__c",
        key: "Status",
        width: "fit-content",
        filters: [
          { text: "Open", value: "Open" },
          { text: "Closed", value: "Closed" },
          { text: "Draft", value: "Draft" },
          { text: "Paused", value: "Paused" },
          { text: "Hidden", value: "Hidden" },
        ],
        onFilter: (value, record) => record.Status__c === value,
        sorter: (a, b) => a.Status__c.localeCompare(b.Status__c),
        render: (text, rec) => (
          <Tag
            color={
              rec.Status__c === "Open"
                ? "green"
                : rec.Status__c === "Closed"
                  ? "red"
                  : "blue"
            }
          >
            {text || rec.Status__c}
          </Tag>
        ),
      },
      {
        title: "Name",
        dataIndex: "Name",
        key: "Name",
        width: "fit-content",
        ellipsis: true,
        sorter: (a, b) => a.Name.localeCompare(b.Name),
        render: (text, rec) => (
          <Link
            to={{ pathname: `/po/${rec.Id}` }}
            state={{ crumbLabel: rec.Name }}
            onClick={() => {
              record({
                id: String(rec.Id),
                title: String(text || rec.Name || rec.Id),
                url: `/po/${rec.Id}`,
                type: "Project Objective",
              });
            }}
          >
            {text || rec.Id}
          </Link>
        ),
      },
      {
        title: "Description",
        dataIndex: "Project_Objective_Description__c",
        key: "Project_Objective_Description__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) =>
          text || rec.Project_Objective_Description__c || "-",
      },
      {
        title: "Target HC",
        dataIndex: "Target_HC__c",
        key: "Target_HC__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.Target_HC__c || "-",
      },
      {
        title: "# Invited",
        dataIndex: "Invited_Contributors__c",
        key: "Invited_Contributors__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.Invited_Contributors__c || "-",
      },
      {
        title: "# Applications",
        dataIndex: "App_Received__c",
        key: "App_Received__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.App_Received__c || "-",
      },
      {
        title: "# Matched",
        dataIndex: "Matched_Contributors__c",
        key: "Matched_Contributors__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.Matched_Contributors__c || "-",
      },
      {
        title: "# Qualified",
        dataIndex: "Qualified_Contributors__c",
        key: "Qualified_Contributors__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.Qualified_Contributors__c || "-",
      },
      {
        title: "# Active",
        dataIndex: "Active_Contributors__c",
        key: "Active_Contributors__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.Active_Contributors__c || "-",
      },
      {
        title: "# Production",
        dataIndex: "Production_Contributors__c",
        key: "Production_Contributors__c",
        width: "fit-content",
        ellipsis: true,
        render: (text, rec) => text || rec.Production_Contributors__c || "-",
      },
    ],
    [navigate, record],
  );

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadAll(); // Reload the list after successful creation
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-4">
        <Typography.Title level={3} style={{ margin: 0 }}>
          Project Objectives
        </Typography.Title>
        <div className="flex items-center gap-3">
          <Button
            type="primary"
            onClick={() => setShowCreateModal(true)}
            icon={
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            }
          >
            Create
          </Button>
          <Button
            type="default"
            onClick={() => setShowCloneModal(true)}
            icon={<CopyOutlined />}
          >
            Clone Objective
          </Button>
          <div className="relative" style={{ maxWidth: 360 }}>
            <Input.Search
              placeholder="Search objectives"
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
            />
            {searchOpen && query.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                {searchLoading ? (
                  <div className="p-3 text-sm text-gray-500">
                    <Spin size="small" /> Searching…
                  </div>
                ) : query.length < 3 ? (
                  <div className="p-3 text-sm text-gray-500">
                    Enter at least 3 characters to search
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No objectives found"
                    />
                  </div>
                ) : (
                  <List
                    dataSource={searchResults}
                    size="large"
                    renderItem={(item) => (
                      <List.Item
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onMouseDown={() => {
                          setQuery("");
                          setSearchResults([]);
                          setSearchOpen(false);
                          record({
                            id: String(item.Id),
                            title: String(item.Name || item.Id),
                            url: `/po/${item.Id}`,
                            type: "ProjectObjective",
                          });
                          navigate(`/po/${item.Id}`);
                        }}
                      >
                        <div className="w-full">
                          <div className="text-gray-900 text-sm font-medium">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500">{item.Id}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clone Project Objective Modal */}
      <CloneProjectObjectiveModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onSuccess={() => {
          setShowCloneModal(false);
          loadAll(); // Refresh the list
        }}
      />
      <Table
        loading={loading}
        rowKey={(r) => r.Id}
        dataSource={records}
        columns={columns}
        scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50", "100"],
          onShowSizeChange: (current, size) => setPageSize(size),
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      {!loading && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Alert
            type="info"
            message={
              <Typography.Text>
                <strong>NOTE :</strong>
                <> Only first 1000 records are being Displayed.</>
              </Typography.Text>
            }
            icon={<InfoCircleOutlined />}
            style={{ width: "fit-content", alignSelf: "flex-end" }}
            showIcon
          />
        </div>
      )}
      <CreateProjectObjectiveModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
